// # Filename: src/components/poker/TabletPokerTable.jsx
// Tablet (640-1024px) table layout: mobile's compact circular seat markers and narrow
// stadium track, scaled up (bigger avatars/cards/badges, cqw-responsive instead of
// mobile's fixed pixel sizes) and with the controls panel flowing normally below the
// table instead of docked to the viewport edge. No table chat at this width -- not
// enough room for table + controls + a chat sidebar without feeling cramped.

import React, { useCallback, useLayoutEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import PokerCard from "./PokerCard";
import PlayerAvatarFace from "./shared/PlayerAvatarFace";
import SeatRoleChip from "./shared/SeatRoleChip";
import ChipTransferOverlay from "./shared/ChipTransferOverlay";
import { FeltInfoPanel, FeltRoundMeta } from "./shared/PokerFeltInfo";
import PokerActionControls from "./shared/PokerActionControls";
import { stadiumPerimeter, stadiumPointAtFraction, opponentSeatFraction } from "./shared/stadiumMath";
import { usePokerTableView } from "./hooks/usePokerTableView";

// Same stadium proportions as mobile (a deliberate choice -- "mobile's compact style,
// scaled up", not a redesign) -- only the container's absolute pixel size and the
// seat/card/badge sizes inside it are bigger.
const TABLET_TABLE_STRAIGHT_HALF_PCT = 12;
const TABLET_TABLE_RADIUS_PCT = 30;
const TABLET_TABLE_HORIZONTAL_RADIUS_SCALE = 1.36;
const TABLET_TABLE_HERO_FRACTION = (2 * TABLET_TABLE_STRAIGHT_HALF_PCT + (Math.PI * TABLET_TABLE_RADIUS_PCT) / 2) /
  stadiumPerimeter(TABLET_TABLE_STRAIGHT_HALF_PCT, TABLET_TABLE_RADIUS_PCT);
const TABLET_TABLE_ASPECT_RATIO = 2 / 3;

// Fallback used only until the page root + controls row report their real measured
// heights (below) -- avoids a layout flash on first paint. See DesktopPokerTable.jsx
// for the full rationale (this mirrors that same "measure real space, don't guess"
// approach, since the controls row flows below the table here too and has to be
// accounted for the same way).
const TABLET_TABLE_VERTICAL_RESERVE_FALLBACK_PX = 260;
const TABLET_TABLE_ROW_GAP_RESERVE_PX = 12;

function tabletTableWidthForReserve(availableHeightPx, localReservePx) {
  return `min(88vw, clamp(420px, 88vw, 820px), calc((${availableHeightPx}px - ${localReservePx}px) * ${1 / TABLET_TABLE_ASPECT_RATIO}))`;
}

function tabletStadiumPositionStyle(point) {
  return {
    left: `${50 + point.yPct * TABLET_TABLE_HORIZONTAL_RADIUS_SCALE}%`,
    top: `${50 + point.xPct}%`,
  };
}

function tabletStadiumPositionPoint(point) {
  return {
    xPct: 50 + point.yPct * TABLET_TABLE_HORIZONTAL_RADIUS_SCALE,
    yPct: 50 + point.xPct,
  };
}

function tabletSeatPoint(index, count) {
  const fraction = opponentSeatFraction(index, count, TABLET_TABLE_HERO_FRACTION);
  return stadiumPointAtFraction(fraction, TABLET_TABLE_STRAIGHT_HALF_PCT, TABLET_TABLE_RADIUS_PCT);
}

function tabletHeroSeatPositionStyle() {
  return tabletStadiumPositionStyle(
    stadiumPointAtFraction(TABLET_TABLE_HERO_FRACTION, TABLET_TABLE_STRAIGHT_HALF_PCT, TABLET_TABLE_RADIUS_PCT)
  );
}

function tabletPocketCardsClassForPoint(point) {
  return point?.yPct > 12 ? "-left-11 top-1/2 -translate-y-1/2" : "-right-11 top-1/2 -translate-y-1/2";
}

function TabletPocketCards({ show, className = "" }) {
  if (!show) return null;
  return (
    <div className={["pointer-events-none absolute z-10 flex gap-1", className].join(" ")}>
      {[0, 1].map((idx) => (
        <div key={idx} className={["origin-center", idx === 1 ? "-ml-4 rotate-6" : "-rotate-6"].join(" ")}>
          <PokerCard card="??" mini faceDown />
        </div>
      ))}
    </div>
  );
}

function TabletTableSeatMarkers({
  opponents,
  currentTurn,
  dealerSeat,
  smallBlindSeat,
  bigBlindSeat,
  chipCountOverrides = {},
  payoutPulseSeats = new Set(),
  registerStackAmountRef,
}) {
  if (!opponents.length) return null;

  return (
    <div className="pointer-events-none absolute inset-0 z-20">
      {opponents.slice(0, 8).map((player, idx) => {
        const seatPoint = tabletSeatPoint(idx, opponents.length);
        const active = Number(currentTurn) === Number(player.seat);
        const stillInHand = !player.folded;
        const dealer = Number(dealerSeat) === Number(player.seat);
        const payoutPulse = payoutPulseSeats.has(Number(player.seat));
        const roleLabel = dealer
          ? "D"
          : Number(player.seat) === Number(smallBlindSeat)
            ? "SB"
            : Number(player.seat) === Number(bigBlindSeat)
              ? "BB"
              : "";
        return (
          <div
            key={player.seat}
            className={[
              "absolute flex h-[clamp(68px,9.2cqw,96px)] w-[clamp(68px,9.2cqw,96px)] -translate-x-1/2 -translate-y-1/2 flex-col items-center justify-center rounded-full border px-1.5 py-1 text-center",
              active
                ? "border-emerald-200/60 bg-emerald-950/90 shadow-[0_0_20px_rgba(16,185,129,0.32),0_8px_18px_rgba(0,0,0,0.34)]"
                : payoutPulse
                  ? "border-amber-200/60 bg-amber-950/88 shadow-[0_0_22px_rgba(251,191,36,0.4),0_8px_18px_rgba(0,0,0,0.34)]"
                : stillInHand
                  ? "border-cyan-200/28 bg-slate-950/82 shadow-[0_0_14px_rgba(34,211,238,0.18),0_8px_18px_rgba(0,0,0,0.3)]"
                  : "border-slate-500/18 bg-slate-950/58 shadow-[0_8px_18px_rgba(0,0,0,0.24)]",
              player.folded ? "opacity-50" : "",
            ].join(" ")}
            style={tabletStadiumPositionStyle(seatPoint)}
          >
            <SeatRoleChip label={roleLabel} className="-right-1 -top-1" elastic />
            {player.bet ? (
              <span className="absolute left-1/2 top-0 -translate-x-1/2 -translate-y-[82%] rounded-full bg-cyan-100 px-2.5 py-1 text-[11px] font-black leading-none text-slate-950">
                {player.bet}
              </span>
            ) : null}
            <div
              className={[
                "relative rounded-full p-[2px]",
                active
                  ? "animate-pulse bg-emerald-200 shadow-[0_0_16px_rgba(110,231,183,0.9)]"
                  : stillInHand
                    ? "bg-cyan-300/85 shadow-[0_0_10px_rgba(34,211,238,0.48)]"
                    : "bg-slate-600/35 opacity-70 grayscale",
              ].join(" ")}
            >
              <PlayerAvatarFace seat={player.seat} />
              <TabletPocketCards show={stillInHand} className={tabletPocketCardsClassForPoint(seatPoint)} />
              {active ? (
                <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border border-slate-950 bg-emerald-300 shadow-[0_0_10px_rgba(110,231,183,0.85)]" />
              ) : null}
            </div>
            <div className="mt-1 w-full truncate text-[10px] font-black uppercase leading-none tracking-[0.02em] text-emerald-50">
              {player.name || "Player"}
            </div>
            <div
              ref={registerStackAmountRef(player.seat)}
              className="mt-0.5 w-full truncate text-[10px] font-bold leading-none text-amber-100"
            >
              {chipCountOverrides[player.seat] ?? player.chips}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function TabletHeroSeatMarker({
  player,
  active,
  handNumber,
  roleLabel,
  timerRemaining,
  timerPercent,
  timerUrgent,
  pendingLabel = "",
  displayChips = null,
  payoutPulse = false,
  stackAmountRef = null,
}) {
  if (!player) return null;
  const showTimer = active && !pendingLabel && timerRemaining != null;
  const stillInHand = !player.folded;

  return (
    <div
      className="pointer-events-none absolute z-30 flex -translate-x-1/2 -translate-y-[58%] flex-col items-center"
      style={tabletHeroSeatPositionStyle()}
    >
      <div className="relative flex justify-center gap-1.5">
        <SeatRoleChip label={roleLabel} className="z-40 -left-7 top-1/2 -translate-y-1/2" elastic />
        {(player.cards || []).map((card, idx) => (
          <PokerCard
            key={`${handNumber}-${player.seat || "me"}-hero-${idx}-${card}`}
            card={card}
            small
            animate={Boolean(card)}
            dealVariant="hole"
            delay={idx * 110 + 40}
          />
        ))}
        {player.folded ? (
          <span className="absolute left-1/2 top-1/2 z-40 -translate-x-1/2 -translate-y-1/2 -rotate-12 rounded-sm border border-rose-200/35 bg-rose-950/82 px-2 py-0.5 text-[9px] font-black uppercase tracking-[0.16em] text-rose-100 shadow-[0_6px_18px_rgba(0,0,0,0.42)]">
            Folded
          </span>
        ) : null}
      </div>
      <div
        className={[
          "relative mt-2.5 flex h-[clamp(76px,10.5cqw,110px)] w-[clamp(76px,10.5cqw,110px)] flex-col items-center justify-center rounded-full border px-1.5 py-1 text-center shadow-[0_10px_22px_rgba(0,0,0,0.32)]",
          active
            ? "border-emerald-200/60 bg-emerald-950/90 shadow-[0_0_20px_rgba(16,185,129,0.32),0_8px_18px_rgba(0,0,0,0.34)]"
            : payoutPulse
              ? "border-amber-200/60 bg-amber-950/88 shadow-[0_0_22px_rgba(251,191,36,0.4),0_10px_22px_rgba(0,0,0,0.32)]"
            : stillInHand
              ? "border-cyan-200/28 bg-slate-950/82 shadow-[0_0_14px_rgba(34,211,238,0.18),0_8px_18px_rgba(0,0,0,0.3)]"
              : "border-slate-500/18 bg-slate-950/58 opacity-50 shadow-[0_8px_18px_rgba(0,0,0,0.24)]",
        ].join(" ")}
      >
        {player.bet ? (
          <span className="absolute left-1/2 top-0 -translate-x-1/2 -translate-y-[82%] rounded-full bg-cyan-100 px-2.5 py-1 text-[11px] font-black leading-none text-slate-950">
            {player.bet}
          </span>
        ) : null}
        <div
          className={[
            "relative rounded-full p-[2px]",
            active
              ? "animate-pulse bg-emerald-200 shadow-[0_0_16px_rgba(110,231,183,0.9)]"
              : stillInHand
                ? "bg-cyan-300/85 shadow-[0_0_10px_rgba(34,211,238,0.48)]"
                : "bg-slate-600/35 opacity-70 grayscale",
          ].join(" ")}
        >
          <PlayerAvatarFace seat={player.seat} isMe />
          <TabletPocketCards show={stillInHand} className="-right-9 top-1/2 -translate-y-1/2" />
          {active ? (
            <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border border-slate-950 bg-emerald-300 shadow-[0_0_10px_rgba(110,231,183,0.85)]" />
          ) : null}
        </div>
        <div className="mt-1 w-full truncate text-[10px] font-black uppercase leading-none tracking-[0.02em] text-emerald-50">
          You
        </div>
        <div ref={stackAmountRef} className="mt-0.5 w-full truncate text-[10px] font-bold leading-none text-amber-100">
          {displayChips ?? player.chips}
        </div>
      </div>
      {showTimer ? (
        <div className="mt-1.5 w-14 overflow-hidden rounded-full bg-black/45">
          <div
            className={[timerUrgent ? "bg-rose-300" : "bg-emerald-200", "h-1 rounded-full transition-[width] duration-200"].join(" ")}
            style={{ width: `${timerPercent}%` }}
          />
        </div>
      ) : null}
    </div>
  );
}

function useTabletTableSizing() {
  const [availableHeightPx, setAvailableHeightPx] = useState(() => (
    typeof window !== "undefined" ? window.innerHeight : 900
  ));
  const [chromeReservePx, setChromeReservePx] = useState(TABLET_TABLE_VERTICAL_RESERVE_FALLBACK_PX);
  const pageRootRef = useRef(null);
  const controlsRowRef = useRef(null);

  useLayoutEffect(() => {
    const node = pageRootRef.current;
    if (!node || typeof ResizeObserver === "undefined") return undefined;
    const measure = () => {
      const height = node.getBoundingClientRect().height;
      if (height) setAvailableHeightPx(height);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useLayoutEffect(() => {
    const node = controlsRowRef.current;
    if (!node || typeof ResizeObserver === "undefined") return undefined;
    const measure = () => {
      const height = node.getBoundingClientRect().height;
      if (!height) return;
      setChromeReservePx(height + TABLET_TABLE_ROW_GAP_RESERVE_PX);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return { pageRootRef, controlsRowRef, tabletTableWidth: tabletTableWidthForReserve(availableHeightPx, chromeReservePx) };
}

export default function TabletPokerTable({ game, handResultAnimation = null, onAction, onNextHand, onPlayAgain }) {
  const navigate = useNavigate();
  const { pageRootRef, controlsRowRef, tabletTableWidth } = useTabletTableSizing();

  const getChipFallbackPoint = useCallback(({ seat, isHero, opponentIndex, opponentCount }) => {
    if (isHero) {
      return tabletStadiumPositionPoint(
        stadiumPointAtFraction(TABLET_TABLE_HERO_FRACTION, TABLET_TABLE_STRAIGHT_HALF_PCT, TABLET_TABLE_RADIUS_PCT)
      );
    }
    return tabletStadiumPositionPoint(tabletSeatPoint(opponentIndex, opponentCount));
  }, []);

  const view = usePokerTableView({ game, handResultAnimation, onAction, getChipFallbackPoint });
  const {
    tableRef,
    registerStackAmountRef,
    me,
    heroEliminated,
    heroFeltPlayer,
    opponents,
    displayedCommunityCards,
    chipCountOverrides,
    potCountOverride,
    chipTransferStreams,
    payoutPulseSeats,
    isMyTurn,
    minRaiseTo,
    maxRaiseTo,
    timerRemaining,
    timerPercent,
    timerUrgent,
    smallBlindSeat,
    bigBlindSeat,
    roleForSeat,
    pendingLabelForSeat,
    can,
    raiseTo,
    setRaiseTo,
    raisePanelOpen,
    setRaisePanelOpen,
    canRaiseOrAllIn,
    raisePresets,
    handleAction,
  } = view;

  return (
    <div ref={pageRootRef} className="mx-auto flex h-full min-h-0 w-full max-w-[900px] flex-col items-center gap-3">
      <div
        ref={tableRef}
        className="relative mx-auto h-auto min-h-0 flex-none overflow-hidden rounded-full border border-emerald-200/15 bg-[#1b1009] shadow-[0_28px_90px_rgba(0,0,0,0.5)] [container-type:inline-size]"
        style={{
          aspectRatio: TABLET_TABLE_ASPECT_RATIO,
          width: tabletTableWidth,
        }}
      >
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_24%,rgba(120,64,24,0.5),transparent_34%),linear-gradient(90deg,rgba(40,20,10,0.96),rgba(88,45,19,0.88),rgba(28,14,8,0.96))]" />
        <div className="absolute inset-3 rounded-full border-[8px] border-[#8a4b18] bg-[radial-gradient(ellipse_at_50%_48%,rgba(22,101,52,0.98),rgba(5,76,44,0.94)_52%,rgba(3,49,32,0.98)_100%)] shadow-[inset_0_0_0_2px_rgba(250,204,21,0.25),inset_0_0_0_8px_rgba(15,23,42,0.28),inset_0_24px_58px_rgba(0,0,0,0.4),0_18px_38px_rgba(0,0,0,0.5)]" />

        <TabletTableSeatMarkers
          opponents={opponents}
          currentTurn={game?.current_turn}
          dealerSeat={game?.dealer}
          smallBlindSeat={smallBlindSeat}
          bigBlindSeat={bigBlindSeat}
          chipCountOverrides={chipCountOverrides}
          payoutPulseSeats={payoutPulseSeats}
          registerStackAmountRef={registerStackAmountRef}
        />
        {!heroEliminated ? (
          <TabletHeroSeatMarker
            player={heroFeltPlayer}
            active={isMyTurn}
            handNumber={game?.hand_number || 1}
            roleLabel={roleForSeat(me?.seat)}
            timerRemaining={timerRemaining}
            timerPercent={timerPercent}
            timerUrgent={timerUrgent}
            pendingLabel={pendingLabelForSeat(me?.seat)}
            displayChips={chipCountOverrides[me?.seat]}
            payoutPulse={payoutPulseSeats.has(Number(me?.seat))}
            stackAmountRef={registerStackAmountRef(me?.seat)}
          />
        ) : null}

        <div className="pointer-events-none absolute inset-0 z-10">
          <div className="absolute left-1/2 top-[calc(50%-60px)] -translate-x-1/2">
            <FeltRoundMeta game={game} />
          </div>
          <div className="absolute left-1/2 top-1/2 flex min-h-[96px] -translate-x-1/2 -translate-y-1/2 items-center justify-center gap-2 px-2 py-3">
            {Array.from({ length: 5 }).map((_, idx) => (
              <PokerCard
                key={`${game?.hand_number || 1}-${idx}-${displayedCommunityCards?.[idx] || "empty"}`}
                card={displayedCommunityCards?.[idx]}
                small
                animate={Boolean(displayedCommunityCards?.[idx])}
                dealVariant="community"
                delay={idx * 90}
              />
            ))}
          </div>
          <div className="absolute left-1/2 top-[calc(50%+56px)] -translate-x-1/2">
            <FeltInfoPanel game={game} displayPot={potCountOverride} />
          </div>
        </div>
        <ChipTransferOverlay streams={chipTransferStreams} />
      </div>

      <div ref={controlsRowRef} className="w-full max-w-[600px]">
        <PokerActionControls
          game={game}
          raiseTo={raiseTo}
          setRaiseTo={setRaiseTo}
          raisePanelOpen={raisePanelOpen}
          setRaisePanelOpen={setRaisePanelOpen}
          raisePresets={raisePresets}
          minRaiseTo={minRaiseTo}
          maxRaiseTo={maxRaiseTo}
          can={can}
          canRaiseOrAllIn={canRaiseOrAllIn}
          handleAction={handleAction}
          onNextHand={onNextHand}
          onPlayAgain={onPlayAgain}
          navigate={navigate}
        />
      </div>
    </div>
  );
}
